import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Shield, Music, DollarSign, Users, FileText, Clock } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

const FAQPage = () => {
  const [openItems, setOpenItems] = useState(new Set([1])); // First item open by default

  React.useEffect(() => {
    document.title = "Frequently Asked Questions | ItzGreen Music";
  }, []);

  const toggleItem = (id) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const faqCategories = [
    {
      category: "Licensing & Clearance",
      icon: Shield,
      color: "text-[rgb(218,255,1)]",
      questions: [
        {
          id: 1,
          question: "What makes ItzGreen Music's clearance process different?",
          answer: "ItzGreen Music operates under a strict Liability Shield protocol. Every asset in our catalog is 100% 'One-Stop' cleared, meaning we control both Master and Publishing rights. This eliminates the typical sync licensing bottleneck where clients must negotiate with multiple rights holders. Our proprietary intake process ensures complete legal reconciliation via BMI and The MLC before any track enters our Supervisor Vault."
        },
        {
          id: 2,
          question: "How quickly can you clear a track for sync usage?",
          answer: "For tracks already in our Supervisor Vault, clearance is instantaneous. Our pre-cleared catalog means verified network partners receive immediate licensing approval. For new submissions, our standard clearance verification process takes 24-48 hours, including full PRO reconciliation and legal documentation review."
        },
        {
          id: 3,
          question: "What rights are included in your sync licenses?",
          answer: "Our sync licenses typically include worldwide usage rights for the specified media (TV, film, advertising, streaming). Each license is customized based on the client's needs, but standard packages include broadcast, streaming, theatrical, and digital advertising rights. We maintain flexibility for exclusive usage periods and territory restrictions as required."
        },
        {
          id: 4,
          question: "Do you handle international clearances?",
          answer: "Yes. Our Liability Shield protocol includes international rights clearance. We work directly with BMI's international affiliates and maintain relationships with major PROs globally (PRS, GEMA, JASRAC, etc.) to ensure seamless worldwide clearance for our network partners."
        }
      ]
    },
    {
      category: "Artist Roster & Submissions",
      icon: Users,
      color: "text-blue-400",
      questions: [
        {
          id: 5,
          question: "How do I submit music to ItzGreen Music?",
          answer: "Submissions are processed through our secure Artist Intake Portal (/intake). You'll need to provide legal name, artist name, PRO affiliation details, and upload both a 24-bit WAV master and ZIP stem folder. Most importantly, you must certify that your audio is 100% human-produced, contains zero uncleared samples, and that you control 100% of Master and Publishing rights."
        },
        {
          id: 6,
          question: "What are your requirements for roster consideration?",
          answer: "We seek professional-grade production quality, complete rights ownership, and PRO affiliation (BMI, ASCAP, or SESAC preferred). All submissions must be 100% human-produced with zero AI-generated content. We prioritize genres that align with current network demand: Commercial Lifestyle, Authentic Cinematic Urban, Contemporary R&B, and House/Dance."
        },
        {
          id: 7,
          question: "Do you accept AI-generated music?",
          answer: "Absolutely not. ItzGreen Music maintains a strict zero-AI policy. All catalog assets must be 100% human-produced. This is a core differentiator for our network partners who require authentic, human-created content for their productions. Any submission found to contain AI-generated elements will be immediately rejected."
        },
        {
          id: 8,
          question: "What happens after I submit my music?",
          answer: "Submissions undergo our Liability Shield verification process, including PRO reconciliation, rights verification, and quality assessment. You'll receive confirmation within 48 hours. Approved tracks enter our Supervisor Vault and become available to verified network partners. We handle all licensing negotiations and provide transparent royalty reporting."
        }
      ]
    },
    {
      category: "Sync Opportunities & Briefs",
      icon: FileText,
      color: "text-orange-400",
      questions: [
        {
          id: 9,
          question: "How do I access active sync briefs?",
          answer: "Active briefs are available to verified roster artists through our secure briefing system. Once you're approved for our roster, you'll receive direct access to current opportunities from Netflix, HBO Max, Apple, Ford, and other major network partners. Brief access is restricted to maintain exclusivity for our clients."
        },
        {
          id: 10,
          question: "What types of sync opportunities do you handle?",
          answer: "We specialize in high-value B2B sync placements: network television series, streaming platform originals, theatrical trailers, and premium brand advertising campaigns. Our focus is on projects with budgets typically ranging from $8,000 to $50,000+ per placement, ensuring meaningful revenue for our roster artists."
        },
        {
          id: 11,
          question: "How are sync fees determined?",
          answer: "Sync fees vary based on usage scope, exclusivity, territory, and client budget. Our network relationships allow us to negotiate premium rates. Typical ranges: TV series ($8,000-$15,000), streaming originals ($12,000-$25,000), major brand campaigns ($20,000-$50,000+), theatrical trailers ($8,000-$20,000). We always negotiate for the highest possible fee."
        },
        {
          id: 12,
          question: "Do you handle performance royalties?",
          answer: "Yes, but indirectly. We ensure all tracks are properly registered with PROs (BMI, ASCAP, SESAC) so artists receive performance royalties directly from their PRO when content airs. We provide detailed usage reports to help artists track and claim their performance royalties. The sync fee and performance royalties are separate revenue streams."
        }
      ]
    },
    {
      category: "Business & Operations",
      icon: DollarSign,
      color: "text-green-400",
      questions: [
        {
          id: 13,
          question: "What is your revenue split with artists?",
          answer: "ItzGreen Music operates on a transparent 50/50 split for sync licensing fees. Artists retain 50% of all sync fees, while ItzGreen retains 50% to cover clearance administration, network relationship management, and business operations. Artists retain 100% of their performance royalties and publishing rights."
        },
        {
          id: 14,
          question: "How quickly do artists receive payment?",
          answer: "Sync fee payments are processed within 30 days of client payment receipt. We provide detailed statements showing gross fees, our administration percentage, and net artist payment. All payments include comprehensive usage documentation for the artist's records and PRO reporting."
        },
        {
          id: 15,
          question: "Do you require exclusive representation?",
          answer: "No. ItzGreen Music operates on a non-exclusive basis. Artists retain the right to work with other sync agents, publishers, or handle direct licensing. However, for tracks in our Supervisor Vault, we do require first right of refusal on sync opportunities that match our network partner briefs."
        },
        {
          id: 16,
          question: "What ongoing support do you provide to roster artists?",
          answer: "Roster artists receive ongoing brief access, sync opportunity notifications, detailed placement reports, and direct communication with our A&R team. We also provide guidance on production trends, network preferences, and strategic catalog development to maximize sync potential."
        }
      ]
    },
    {
      category: "Technical & Legal",
      icon: Music,
      color: "text-purple-400",
      questions: [
        {
          id: 17,
          question: "What audio formats do you require?",
          answer: "We require 24-bit WAV masters at 44.1kHz or higher sample rates. Additionally, we need complete stem folders (drums, bass, keys, vocals, etc.) in ZIP format for mixing flexibility. This allows our network partners to customize tracks for their specific editorial needs while maintaining professional audio quality."
        },
        {
          id: 18,
          question: "What metadata do you need for submissions?",
          answer: "Essential metadata includes: track title, artist name, legal name, PRO affiliation, IPI number (if applicable), genre/mood descriptors, BPM, key signature, and detailed instrumentation. Accurate metadata is crucial for our search and discovery systems used by music supervisors."
        },
        {
          id: 19,
          question: "How do you handle copyright and publishing administration?",
          answer: "Artists retain 100% ownership of their copyrights and publishing rights. ItzGreen Music acts as a sync licensing agent, not a publisher. We handle sync licensing administration only, ensuring artists maintain full control over their intellectual property while benefiting from our network relationships and clearance expertise."
        },
        {
          id: 20,
          question: "What legal protections do you provide?",
          answer: "Our Liability Shield protocol provides comprehensive legal protection for both artists and clients. We maintain errors & omissions insurance, require detailed rights documentation, and conduct thorough clearance verification. This protects all parties from potential legal disputes and ensures clean, defensible licensing."
        }
      ]
    }
  ];

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO ABOUT
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <HelpCircle size={12} className="mr-2" />
            KNOWLEDGE BASE
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            Frequently Asked Questions
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-lg leading-relaxed max-w-3xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Comprehensive answers to common questions about ItzGreen Music's sync licensing services, artist roster, and B2B operations.
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl overflow-hidden"
            >
              {/* Category Header */}
              <div className="p-6 border-b border-[rgba(255,255,255,0.08)]">
                <div className="flex items-center gap-3">
                  <category.icon size={20} className={category.color} />
                  <h2
                    className="text-white text-xl font-bold"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {category.category}
                  </h2>
                </div>
              </div>

              {/* Questions */}
              <div className="divide-y divide-[rgba(255,255,255,0.05)]">
                {category.questions.map((item) => (
                  <div key={item.id}>
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full p-6 text-left hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <h3
                          className="text-white text-base font-medium pr-4"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.question}
                        </h3>
                        {openItems.has(item.id) ? (
                          <ChevronUp size={20} className="text-[rgb(161,161,170)] flex-shrink-0" />
                        ) : (
                          <ChevronDown size={20} className="text-[rgb(161,161,170)] flex-shrink-0" />
                        )}
                      </div>
                    </button>
                    
                    {openItems.has(item.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="px-6 pb-6"
                      >
                        <p
                          className="text-[rgb(200,200,210)] text-sm leading-relaxed"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <Separator className="bg-[rgb(63,63,63)] my-12" />

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-[rgba(218,255,1,0.05)] border border-[rgba(218,255,1,0.2)] rounded-xl p-6 text-center"
        >
          <h3
            className="text-white text-lg font-bold mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            STILL HAVE QUESTIONS?
          </h3>
          <p
            className="text-[rgb(161,161,170)] text-sm mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Can't find what you're looking for? Contact our team directly for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:leaver@itzgreenmusic.com"
              className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-bold tracking-wider px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              EMAIL DIRECTLY
            </a>
            <Link
              to="/licensing"
              className="inline-flex items-center gap-2 border-2 border-[rgb(218,255,1)] text-[rgb(218,255,1)] text-sm font-bold tracking-wider px-6 py-3 rounded-xl hover:bg-[rgba(218,255,1,0.1)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              LICENSING INQUIRY
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default FAQPage;